// components/system/SystemControl.jsx
import React, { useState, useEffect } from 'react';
import { RefreshCw, Settings, Clock } from 'lucide-react';
import { useSystemStatus } from '../../hooks/useSystemStatus';
import { 
    formatTimestamp, 
    formatDateDDMMYYYY,
    getDataFreshnessStatus, 
    getRefreshStatusIcon,
    formatDatasetInfo,
    getDatasetHealthStatus
} from '../../utils/systemUtils';
import { 
    PUBLISHER_CLASSES, 
    NOTIFICATION_CONFIG
} from '../../constants/systemConstants';
import StatusCard from './StatusCard';
import ProgressIndicator from './ProgressIndicator';
import RefreshControlPanel from './RefreshControlPanel';
import AnalysisConfigPanel from './AnalysisConfigPanel';
import RefreshHistoryTable from './RefreshHistoryTable';

const SystemControl = () => {
    const { systemStatus, refreshProgress, actions } = useSystemStatus();
    const [selectedPublisherClasses, setSelectedPublisherClasses] = useState(PUBLISHER_CLASSES.ALL);
    const [notification, setNotification] = useState(null);

    const showNotification = (message, type = NOTIFICATION_CONFIG.TYPES.INFO) => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), NOTIFICATION_CONFIG.AUTO_DISMISS_DELAY);
    };

    // Fetch system status when component mounts
    useEffect(() => {
        actions.refreshSystemStatus(true);
    }, [actions.refreshSystemStatus]); // Depend only on the specific action

    // Fetch pipeline status on mount
    useEffect(() => {
        actions.getPipelineStatus();
    }, [actions]);

    // Poll pipeline status while it is running
    useEffect(() => {
        if (refreshProgress.source === 'pipeline' && refreshProgress.isRunning) {
            const interval = setInterval(() => {
                actions.getPipelineStatus();
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [refreshProgress.source, refreshProgress.isRunning, actions]);

    const handleRefresh = async (options, refreshLabel) => {
        const result = await actions.startRefresh(options);
        if (result.success) {
            showNotification(`${refreshLabel} started successfully`, NOTIFICATION_CONFIG.TYPES.SUCCESS);
        } else {
            showNotification(`Failed to start refresh: ${result.error}`, NOTIFICATION_CONFIG.TYPES.ERROR);
        }
    };

    const handleStopRefresh = async () => {
        const result = await actions.stopRefresh();
        if (result.success) {
            showNotification('Refresh stop signal sent', NOTIFICATION_CONFIG.TYPES.SUCCESS);
        } else {
            showNotification(`Failed to stop refresh: ${result.error}`, NOTIFICATION_CONFIG.TYPES.ERROR);
        }
    };

    const handleGenerateDatasets = async () => {
        const result = await actions.generateDatasets();
        if (result.success) {
            showNotification('Dataset generation started successfully', NOTIFICATION_CONFIG.TYPES.SUCCESS);
        } else {
            showNotification(`Failed to generate datasets: ${result.error}`, NOTIFICATION_CONFIG.TYPES.ERROR);
        }
    };

    const isInitialLoading = systemStatus.loading;
    const isRefreshingStatus = systemStatus.refreshing;

    const dataFreshness = (() => {
        if (!systemStatus.dataFreshness) {
            return { status: 'unknown', color: 'gray', message: 'No data available' };
        }

        if (systemStatus.dataFreshness.status === 'error') {
            return { status: 'error', color: 'red', message: 'Data freshness service unavailable' };
        }

        // If we have a lastUpdate timestamp, use it with proper date formatting
        if (systemStatus.dataFreshness.lastUpdate) {
            const freshnessResult = getDataFreshnessStatus(systemStatus.dataFreshness.lastUpdate);
            const formattedDate = formatDateDDMMYYYY(systemStatus.dataFreshness.lastUpdate);
            
            return {
                ...freshnessResult,
                message: `Date: ${formattedDate} - ${freshnessResult.message}`
            };
        }

        // Try to find the most recent refresh date from refresh history
        if (systemStatus.refreshHistory && systemStatus.refreshHistory.length > 0) {
            const mostRecentRefresh = systemStatus.refreshHistory
                .filter(item => item.last_success)
                .sort((a, b) => new Date(b.last_success) - new Date(a.last_success))[0];
            
            if (mostRecentRefresh) {
                const formattedDate = formatDateDDMMYYYY(mostRecentRefresh.last_success);
                const freshnessResult = getDataFreshnessStatus(mostRecentRefresh.last_success);
                return {
                    ...freshnessResult,
                    message: `Date: ${formattedDate} - ${freshnessResult.message}`
                };
            }
        }

        // If no lastUpdate but we have detailed freshness info, use that
        const details = systemStatus.dataFreshness.details;
        if (details) {
            const { publisherCache, marketStatistics } = details;
            
            // Determine overall freshness based on components
            if (publisherCache?.freshness === 'RECENT' && marketStatistics?.freshness === 'FRESH') {
                return { status: 'fresh', color: 'green', message: 'Date: unknown - data is fresh' };
            } else if (publisherCache?.freshness === 'RECENT' || marketStatistics?.freshness === 'RECENT') {
                return { status: 'stale', color: 'yellow', message: 'Date: unknown - some data getting stale' };
            } else if (publisherCache?.freshness === 'STALE' || marketStatistics?.freshness === 'STALE') {
                return { status: 'old', color: 'orange', message: 'Date: unknown - data is stale' };
            }
        }

        // We have freshness service working but no previous refresh
        if (systemStatus.dataFreshness.needsRefresh) {
            return { 
                status: 'needs_refresh', 
                color: 'orange', 
                message: 'Date: unknown - refresh needed' 
            };
        }

        // Fallback
        return { status: 'unknown', color: 'gray', message: 'Date: unknown - status unclear' };
    })();

    // Smart refresh recommendation display
    const smartRecommendation = systemStatus.dataFreshness?.recommendation;
    
    // Enhanced dataset information
    const datasetInfo = formatDatasetInfo(systemStatus.databaseHealth?.postgresql?.datasets);
    const datasetHealth = getDatasetHealthStatus(datasetInfo);

    // Extract stale publisher classes from freshness data
    const stalePublisherClasses = systemStatus.dataFreshness?.details?.publisherClassesToRefresh || [];
    
    return (
        <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold mb-2">System Control</h2>
                    <div className="flex items-center space-x-3 text-gray-600">
                        <p>Monitor and manage data refresh operations</p>
                        {(isInitialLoading || isRefreshingStatus) && (
                            <span className="inline-flex items-center space-x-2 text-sm text-blue-600">
                                <RefreshCw className="w-4 h-4 animate-spin" />
                                <span>{isInitialLoading ? 'Loading system status...' : 'Refreshing status...'}</span>
                            </span>
                        )}
                    </div>
                </div>
                <button
                    onClick={() => actions.refreshSystemStatus()}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-70"
                    disabled={isRefreshingStatus}
                >
                    <RefreshCw className={`w-4 h-4 ${isRefreshingStatus ? 'animate-spin' : ''}`} />
                    <span>Refresh Status</span>
                </button>
            </div>

            {/* Notification */}
            {notification && (
                <div className={`p-4 rounded border-l-4 ${
                    notification.type === 'success' ? 'bg-green-50 border-green-400 text-green-700' :
                    notification.type === 'error' ? 'bg-red-50 border-red-400 text-red-700' :
                    'bg-blue-50 border-blue-400 text-blue-700'
                }`}>
                    {notification.message}
                </div>
            )}

            {/* System Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {isInitialLoading ? (
                    <>
                        <div className="border rounded-lg p-4 bg-gray-50 animate-pulse h-40" />
                        <div className="border rounded-lg p-4 bg-gray-50 animate-pulse h-40" />
                        <div className="border rounded-lg p-4 bg-gray-50 animate-pulse h-40" />
                    </>
                ) : (
                    <>
                        <StatusCard
                            title="Data Freshness"
                            status={dataFreshness.status?.toUpperCase() || 'UNKNOWN'}
                            color={dataFreshness.color}
                            icon="📊"
                            timestamp={systemStatus.dataFreshness?.lastUpdate}
                            details={dataFreshness.message}
                        />
                        
                        <StatusCard
                            title="MariaDB Games"
                            value={systemStatus.databaseHealth?.mariadb?.gameCount}
                            color={systemStatus.databaseHealth?.mariadb?.connected ? 'green' : 'red'}
                            icon="🎮"
                            timestamp={systemStatus.databaseHealth?.mariadb?.lastUpdate}
                            details={systemStatus.databaseHealth?.mariadb?.connected ? 'Connected' : 'Disconnected'}
                        />
                        
                        <StatusCard
                            title="PostgreSQL Datasets"
                            value={datasetInfo.count}
                            color={datasetHealth.color}
                            icon="🎯"
                            timestamp={datasetInfo.latestUpdate}
                            details={[
                                `${datasetHealth.message}`,
                                `Total size: ${datasetInfo.formattedSize}`,
                                `Types: ${datasetInfo.types.join(', ')}`
                            ]}
                        />
                    </>
                )}
            </div>

            {/* Single-Button Pipeline Control */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-5 sm:p-6 shadow-sm">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center space-x-2">
                            <span className="text-2xl">🚀</span>
                            <span>Update Analytics Data</span>
                        </h3>
                        <p className="text-gray-600 mb-1">
                            Complete data pipeline: Refresh game data from Gamalytics + Generate all PostgreSQL datasets
                        </p>
                        <p className="text-sm text-gray-500">
                            ⏱️ Estimated duration: 2-4 hours · Background operation with real-time progress tracking
                        </p>
                    </div>
                    <div className="flex flex-col gap-2 lg:ml-6 lg:items-end">
                        {!refreshProgress.isRunning ? (
                            <button
                                onClick={async () => {
                                    const result = await actions.startPipeline();
                                    if (result.success) {
                                        showNotification('Analytics data update started successfully', NOTIFICATION_CONFIG.TYPES.SUCCESS);
                                        await actions.getPipelineStatus();
                                    } else {
                                        showNotification(`Failed to start: ${result.error}`, NOTIFICATION_CONFIG.TYPES.ERROR);
                                    }
                                }}
                                className="flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 font-semibold text-lg"
                            >
                                <RefreshCw className="w-6 h-6" />
                                <span>Start Update</span>
                            </button>
                        ) : (
                            <button
                                onClick={async () => {
                                    const result = await actions.stopPipeline();
                                    if (result.success) {
                                        showNotification('Pipeline stop signal sent', NOTIFICATION_CONFIG.TYPES.SUCCESS);
                                        await actions.getPipelineStatus();
                                    } else {
                                        showNotification(`Failed to stop: ${result.error}`, NOTIFICATION_CONFIG.TYPES.ERROR);
                                    }
                                }}
                                className="flex items-center space-x-3 px-8 py-4 bg-red-600 text-white rounded-lg hover:bg-red-700 shadow-lg hover:shadow-xl transition-all font-semibold text-lg"
                            >
                                <span className="w-6 h-6">⏹️</span>
                                <span>Stop Update</span>
                            </button>
                        )}
                        <button
                            onClick={handleGenerateDatasets}
                            disabled={refreshProgress.isRunning}
                            className="inline-flex items-center justify-center space-x-2 self-start lg:self-end px-4 py-2 text-sm font-medium text-blue-700 bg-white/90 border border-blue-200 rounded-lg shadow-sm hover:bg-white hover:border-blue-300 hover:text-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Generate PostgreSQL datasets only, without refreshing game data"
                        >
                            <Settings className="w-4 h-4" />
                            <span>Generate Datasets Only</span>
                        </button>
                    </div>
                </div>
            </div>
            
            {/* Active Refresh Progress */}
            {refreshProgress.isRunning && (
                <div className="bg-white border rounded-lg p-6 shadow-sm">
                    <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                        <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
                        <span>Refresh In Progress</span>
                    </h3>
                    <ProgressIndicator
                        progress={refreshProgress.progress}
                        isActive={refreshProgress.isRunning}
                        currentPhase={refreshProgress.currentPhase}
                        details={refreshProgress.details}
                        startTime={refreshProgress.startTime}
                    />
                </div>
            )}

            {/* Control Panels */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Data Refresh Controls */}
                <RefreshControlPanel
                    selectedPublisherClasses={selectedPublisherClasses}
                    onPublisherClassesChange={setSelectedPublisherClasses}
                    onRefresh={handleRefresh}
                    onStopRefresh={handleStopRefresh}
                    isRefreshRunning={refreshProgress.isRunning}
                    smartRecommendation={smartRecommendation}
                    stalePublisherClasses={stalePublisherClasses}
                />

                {/* Analysis Config Management */}
                <AnalysisConfigPanel onNotify={showNotification} />
            </div>

            {/* Current Refresh Status */}
            {isInitialLoading ? (
                <div className="bg-white border rounded-lg p-6 shadow-sm">
                    <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                        <Clock className="w-5 h-5 text-gray-500" />
                        <span>Recent Refresh History</span>
                    </h3>
                    <div className="space-y-3">
                        <div className="h-10 bg-gray-100 rounded animate-pulse" />
                        <div className="h-10 bg-gray-100 rounded animate-pulse" />
                        <div className="h-10 bg-gray-100 rounded animate-pulse" />
                    </div>
                </div>
            ) : (
                <RefreshHistoryTable 
                    refreshHistory={systemStatus.refreshHistory}
                    isRefreshActive={refreshProgress.isRunning}
                    currentPhase={refreshProgress.currentPhase}
                    maxRows={10}
                    title={refreshProgress.isRunning ? "Current Refresh Status" : "Recent Refresh History"}
                />
            )}
        </div>
    );
};

export default SystemControl;
